// Two closed lobes remain valid meshes after the connecting bridge disappears.
// Offsets and anchors use the toy's local coordinates; defaults scale with it.
export class TearPair {
  // stretch lengthens how far the toy pulls out before it tears. elastic (0..1)
  // is how fast a loosened strand pulls itself taut: 1 is springy mochi, near 0
  // is cheese that stays slack and hangs. gravity scales how heavily it hangs.
  // lobe is the share of a pull that the two bodies take by elongating; the rest
  // is drawn out as the bridge, so a small value gives long strings.
  // thin makes the strand narrow faster as it stretches (cheese threads).
  constructor({anchors,scale=1,stretch=1,elastic=1,gravity=1,lobe=.32,thin=1}={}) {
    this.scale=scale;this.stretch=stretch;this.elastic=elastic;this.gravity=gravity;this.lobe=lobe;this.thin=thin;
    this.anchors=(anchors??[[-.004*scale,0,0],[.004*scale,0,0]]).map(p=>[...p]);
    this.restGap=Math.hypot(...this.anchors[1].map((n,i)=>n-this.anchors[0][i]));
    this.radius=.24*scale;this.reach=1.15*scale*stretch;
    this.tearDistance=.95*scale*stretch;this.rejoinDistance=.12*scale;
    this.bridge={start:[0,0,0],end:[0,0,0],endRadius:this.radius,neckRadius:this.radius,visible:true};
    this.strands=[[.15,.07],[-.10,.14],[-.14,-.09],[.09,-.15]].map(([y,z],i)=>({
      offset:[0,y*scale,z*scale],radius:(.065+i*.003)*scale,threshold:[.60,.72,.84,.95][i]*scale*stretch,
      split:[.44,.57,.48,.61][i],intact:true,age:0,cutVector:[0,0,0],
    }));
    this.reset();
  }

  reset() {
    this.positions=[[0,0,0],[0,0,0]];this.velocity=[[0,0,0],[0,0,0]];
    this.surfaceAnchors=this.anchors.map(p=>[...p]);this.lobePull=[0,0,0];
    this.held=null;this.origin=[0,0,0];this.target=[0,0,0];
    this.attached=true;this.healing=1;this.contactTime=0;this.tearCount=0;this.activity=0;
    this.cutCooldown=0;this.fatigue=0;this.tension=0;this.rope={rest:0,base:0,h:0,hv:0};this.chord=0;this.healStrands();
    this.updateBridge();
    this.rope.rest=this.rope.base=this.chord;
  }

  grab(side) {
    if(side!==-1&&side!==1)return;
    this.held=side;this.origin=[...this.positions[side===-1?0:1]];this.target=[...this.origin];
    this.velocity[side===-1?0:1].fill(0);this.activity=1;
  }

  move(delta) {
    if(this.held===null||!delta.every(Number.isFinite))return;
    for(let a=0;a<3;a++)this.target[a]=this.origin[a]+delta[a];
    const length=Math.hypot(...this.target);
    if(length>this.reach)for(let a=0;a<3;a++)this.target[a]*=this.reach/length;
    // Preserve relative order, but allow either piece across its original x=0.
    const other=this.positions[this.held===-1?1:0][0];
    this.target[0]=this.held===-1?Math.min(this.target[0],other):Math.max(this.target[0],other);
    const transverse=Math.hypot(this.target[1],this.target[2]),available=Math.sqrt(Math.max(0,this.reach**2-this.target[0]**2));
    if(transverse>available){this.target[1]*=available/transverse;this.target[2]*=available/transverse;}
    this.activity=1;
  }

  release() {this.held=null;this.activity=1;}
  offset(side) {return [...this.positions[side===-1?0:1]];}

  setSurfaceAnchors(anchors) {
    for(let i=0;i<2;i++)for(let a=0;a<3;a++)this.surfaceAnchors[i][a]=anchors[i][a];
    this.updateBridge();
  }

  // Tension spreads from the joining face through the entire closed lobe.
  // The quadratic axial field has a matching transverse contraction, so a
  // straight pull preserves local volume instead of scaling a rigid half.
  // Input is the cage-deformed point/normal; outputs can reuse those arrays.
  sampleLobe(side,point,out=[0,0,0],normal=null) {
    const index=side===-1?0:1,anchor=this.surfaceAnchors[index],offset=this.positions[index];
    const x=point[0],y=point[1]-anchor[1],z=point[2]-anchor[2],length=.85*this.scale;
    const t=Math.max(0,1-side*(x-anchor[0])/length),weight=t*t;
    const direction=-side,dx=this.lobePull[0]*direction,dy=this.lobePull[1]*direction,dz=this.lobePull[2]*direction;
    const contraction=1/Math.sqrt(1+2*Math.hypot(...this.lobePull)*t/length);
    out[0]=x+offset[0]+dx*weight;
    out[1]=anchor[1]+y*contraction+offset[1]+dy*weight;
    out[2]=anchor[2]+z*contraction+offset[2]+dz*weight;
    if(normal){
      const dw=t>0?-2*side*t/length:0;
      const ds=t>0?side*Math.hypot(...this.lobePull)*contraction**3/(length*length):0;
      const jx=1+dx*dw,jy=y*ds+dy*dw,jz=z*ds+dz*dw;
      const [nx,ny,nz]=normal,s=contraction;
      const a=s*s*nx-s*(jy*ny+jz*nz),b=jx*s*ny,c=jx*s*nz,n=Math.hypot(a,b,c);
      if(n>1e-12){normal[0]=a/n;normal[1]=b/n;normal[2]=c/n;}
    }
    return out;
  }

  step(dt) {
    if(!Number.isFinite(dt)||dt<=0)return;
    const elapsed=Math.min(dt,.05);
    let activity=0;
    for(let i=0;i<2;i++){
      const side=i===0?-1:1,held=this.held===side,p=this.positions[i],v=this.velocity[i];
      if(held||this.attached){
        const omega=held?26:9,decay=Math.exp(-omega*elapsed);
        for(let a=0;a<3;a++){
          const target=held?this.target[a]:0,error=p[a]-target,term=v[a]+omega*error;
          p[a]=target+(error+term*elapsed)*decay;
          v[a]=(v[a]-omega*term*elapsed)*decay;
          if(Math.abs(p[a]-target)<.0001*this.scale&&Math.abs(v[a])<.002*this.scale){p[a]=target;v[a]=0;}
          activity=Math.max(activity,Math.abs(p[a]-target));
        }
      }else{
        // A separated piece settles where it was released instead of auto-healing.
        const decay=Math.exp(-18*elapsed);
        for(let a=0;a<3;a++){p[a]+=v[a]*(1-decay)/18;v[a]*=decay;if(Math.abs(v[a])<.001*this.scale)v[a]=0;}
      }
      const length=Math.hypot(...p);
      if(length>this.reach){for(let a=0;a<3;a++)p[a]*=this.reach/length;v.fill(0);}
      activity=Math.max(activity,Math.hypot(...v));
    }

    if(this.positions[0][0]>this.positions[1][0]){
      // Resolve crossing after both springs move, including an off-center heal.
      const left=this.positions[0],right=this.positions[1];
      const available=Math.sqrt(Math.max(0,Math.min(this.reach**2-left[1]**2-left[2]**2,this.reach**2-right[1]**2-right[2]**2)));
      const x=Math.max(-available,Math.min(available,(left[0]+right[0])*.5));
      left[0]=x;right[0]=x;
      this.velocity[0][0]=this.velocity[1][0]=(this.velocity[0][0]+this.velocity[1][0])*.5;
    }

    const follow=1-Math.exp(-elapsed/(this.attached?.055:.13));
    for(let a=0;a<3;a++){
      const target=this.attached?(this.positions[1][a]-this.positions[0][a])*this.lobe:0;
      const previous=this.lobePull[a];this.lobePull[a]+=(target-previous)*follow;
      if(Math.abs(target-this.lobePull[a])<.00005*this.scale)this.lobePull[a]=target;
      activity=Math.max(activity,Math.abs(this.lobePull[a]-previous)/elapsed,Math.abs(target-this.lobePull[a]));
    }
    this.updateBridge();
    activity=Math.max(activity,this.stepRope(elapsed));
    this.cutCooldown=Math.max(0,this.cutCooldown-elapsed);
    for(const strand of this.strands)if(!strand.intact){
      strand.age=Math.min(this.strandLife,strand.age+elapsed);
      activity=Math.max(activity,1-strand.age/this.strandLife);
    }
    if(this.attached&&this.extension<this.rejoinDistance)this.healStrands();
    // Creep: mochi held under tension keeps flowing and thinning, so a stretch
    // held past about half of the first strand's limit parts a moment later.
    // On a phone the finger often cannot reach the full tear distance.
    // It starts after the stretch has been held for a while, so a quick pull
    // still breaks the strands in stages at their own lengths.
    const strain=this.extension/this.strands[0].threshold;
    if(this.attached&&this.held!==null&&strain>.55){this.tension+=elapsed;if(this.tension>1.2)this.fatigue=Math.min(1,this.fatigue+elapsed*1.6*(strain-.55));}
    else{this.tension=0;this.fatigue=Math.max(0,this.fatigue-elapsed*.5);}
    if(this.attached&&this.cutCooldown===0){
      const give=1-.55*this.fatigue,strand=this.strands.find(s=>s.intact&&this.extension>=s.threshold*give);
      if(strand){
        strand.intact=false;strand.age=0;this.cutCooldown=.045;activity=Math.max(activity,1);
        for(let a=0;a<3;a++)strand.cutVector[a]=this.bridge.end[a]-this.bridge.start[a];
      }
    }
    if(this.attached&&this.strands.every(s=>!s.intact)){
      this.attached=false;this.healing=0;this.contactTime=0;this.tearCount++;
      const length=this.extension+this.restGap;
      for(let i=0;i<2;i++)for(let a=0;a<3;a++){
        // A short tension recoil, bounded by the existing spring and reach.
        const away=(this.bridge.end[a]-this.bridge.start[a])/length*(i===0?-1:1);
        this.velocity[i][a]=Math.max(-this.scale,Math.min(this.scale,this.velocity[i][a]*.45+away*.45*this.scale));
      }
    }else if(!this.attached){
      const speed=Math.hypot(...this.velocity[1].map((n,a)=>n-this.velocity[0][a]));
      // Broad torn faces can meet with a small vertical offset. Requiring
      // identical center anchors makes visibly touching lobes fail to attach.
      const dx=Math.abs(this.positions[1][0]-this.positions[0][0]);
      const transverse=Math.hypot(this.positions[1][1]-this.positions[0][1],this.positions[1][2]-this.positions[0][2]);
      const touching=dx<this.rejoinDistance&&transverse<.42*this.scale;
      this.contactTime=touching&&speed<.7*this.scale?this.contactTime+elapsed:0;
      if(this.contactTime>=.08){this.attached=true;this.healing=0;this.contactTime=0;this.healStrands();}
      else if(this.contactTime>0)activity=Math.max(activity,1);
    }
    if(this.attached&&this.healing<1){this.healing=Math.min(1,this.healing+elapsed/.14);activity=Math.max(activity,1-this.healing);}
    this.updateBridge();
    this.activity=activity;
  }

  updateBridge() {
    const b=this.bridge;
    this.extension=Math.max(0,Math.hypot(...this.anchors[1].map((v,a)=>v+this.positions[1][a]-this.anchors[0][a]-this.positions[0][a]))-this.restGap);
    this.sampleLobe(-1,this.surfaceAnchors[0],b.start);
    this.sampleLobe(1,this.surfaceAnchors[1],b.end);
    this.chord=Math.hypot(b.end[0]-b.start[0],b.end[1]-b.start[1],b.end[2]-b.start[2]);
    const stretch=Math.min(1,this.extension/this.tearDistance);
    // Material that has flowed long stays thin even after the pull is released.
    const excess=Math.max(this.extension,this.rope.rest-this.rope.base);
    // Material feeds the neck from inside both lobes, beyond the tiny rest gap.
    const intact=this.strands.filter(s=>s.intact).length/this.strands.length;
    const narrowing=Math.sqrt((2*this.radius)/(2*this.radius+excess*this.thin))*Math.pow(1-stretch,.7)*Math.pow(intact,.7);
    const blend=this.healing*this.healing*(3-2*this.healing);
    this.rootContraction=1/Math.sqrt(1+2*Math.hypot(...this.lobePull)/(.85*this.scale));
    b.endRadius=this.radius*blend*this.rootContraction;b.neckRadius=b.endRadius*narrowing;
    b.visible=this.attached&&blend>0;
    this.phase=!this.attached?'separated':this.healing<1?'rejoining':this.extension>.025*this.scale?'stretching':'joined';
  }

  get strandLife() {return .45+1.2*(1-this.elastic);}
  sag(t) {return this.rope.h*4*t*(1-t);}

  // Flow and hang. The rest length follows the chord while it is taut, so pulled
  // material keeps its length (plastic flow). Slack then drops under gravity as a
  // damped spring, and springy material shortens back toward the chord.
  stepRope(dt) {
    const rope=this.rope,chord=this.chord;
    if(chord>rope.rest)rope.rest+=(chord-rope.rest)*(1-Math.exp(-14*dt));
    else rope.rest-=(rope.rest-chord)*(1-Math.exp(-this.elastic*9*dt));
    const slack=Math.max(0,rope.rest*rope.rest-chord*chord);
    const target=Math.min(.6*this.scale,.85*Math.sqrt(slack)/2*this.gravity);
    rope.hv+=((target-rope.h)*140-rope.hv*16)*dt;rope.h=Math.max(0,rope.h+rope.hv*dt);
    return Math.abs(target-rope.h)+Math.abs(rope.hv)*.1;
  }

  healStrands() {
    for(const strand of this.strands){strand.intact=true;strand.age=0;strand.cutVector.fill(0);}
    this.cutCooldown=0;this.rope.rest=this.chord;this.rope.h=0;this.rope.hv=0;
  }

  // Intact strands run left to right (side=0). After rupture, sample each
  // closed stub with side=-1/+1, from its lobe root (t=0) to its tip (t=1).
  sampleStrand(index,t,out=[0,0,0,0],side=0) {
    const strand=this.strands[index];t=Math.max(0,Math.min(1,t));
    const b=this.bridge;
    if(strand.intact){
      const blend=this.healing*this.healing*(3-2*this.healing);
      const neck=Math.sqrt(2*strand.radius/(2*strand.radius+this.extension));
      for(let a=0;a<3;a++)out[a]=b.start[a]+(b.end[a]-b.start[a])*t+strand.offset[a]*this.rootContraction;
      out[1]-=this.sag(t);
      out[3]=strand.radius*this.rootContraction*blend*(neck+(1-neck)*Math.cos(Math.PI*t)**2);
    }else{
      const left=side!==1,root=left?b.start:b.end,fraction=left?strand.split:1-strand.split;
      const fullLength=Math.hypot(...strand.cutVector),restLength=(.065+index*.01)*this.scale;
      const recoil=strand.age>=this.strandLife?0:Math.exp(-strand.age/(.07+.6*(1-this.elastic)));
      const length=restLength+(fullLength*fraction-restLength)*recoil;
      for(let a=0;a<3;a++)out[a]=root[a]+strand.offset[a]*this.rootContraction+strand.cutVector[a]/(fullLength||1)*length*t*(left?1:-1);
      out[1]-=Math.sin(Math.PI*t)*.025*this.scale*(1-recoil);
      // A loose stub hangs from its root instead of springing straight back.
      out[1]-=(1-this.elastic)*.5*length*t*t*this.gravity;
      out[3]=strand.radius*this.rootContraction*Math.sqrt(Math.max(0,1-t*t))*(1-.3*t);
    }
    return out;
  }

  // Reuse a four-element output buffer for tube rings: x, y, z, radius.
  sampleBridge(t,out=[0,0,0,0]) {
    t=Math.max(0,Math.min(1,t));
    const b=this.bridge;
    for(let a=0;a<3;a++)out[a]=b.start[a]+(b.end[a]-b.start[a])*t;
    const shoulder=Math.cos(Math.PI*t)**2;
    out[3]=b.neckRadius+(b.endRadius-b.neckRadius)*shoulder;
    out[1]-=this.sag(t);
    return out;
  }
}
